import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts'
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Calendar,
  PieChart as PieChartIcon,
  BarChart3,
  Loader2,
  AlertCircle,
  Download,
  Filter
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api } from "@/services/api";

interface RevenueAnalytics {
  batchWiseRevenue: Array<{
    batchYear: string
    totalRevenue: number
    totalStudents: number
    averageRevenue: number
  }>
  monthlyRevenue: Array<{
    month: string
    year: number
    amount: number
    paymentCount: number
  }>
  programWiseRevenue: Array<{
    programName: string
    programCode: string
    totalRevenue: number
    studentCount: number
    averageFee: number
  }>
  paymentModeBreakdown: Array<{
    paymentMode: string
    amount: number
    count: number
    percentage: number
  }>
}

const AccountantAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<RevenueAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = (await api.get(`financial/revenue-analytics`)).data;
      // const response = await fetch('/api/financial/revenue-analytics', {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   }
      // })

      // if (!response.ok) {
      //   throw new Error('Failed to fetch analytics data')
      // }

      // const data = await response.json()
      if (response.success) {
        setAnalytics(response.data)
      } else {
        setError(response.message || 'Failed to load analytics')
      }
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num)
  }

  // Chart colors
  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16', '#f97316']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <span className="text-gray-600">Loading analytics data...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchAnalytics} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Financial Analytics</h1>
          <p className="text-sm text-gray-600">Comprehensive revenue analysis and financial insights</p>
        </div>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="batch-analysis">Batch Analysis</TabsTrigger>
          <TabsTrigger value="program-analysis">Program Analysis</TabsTrigger>
          <TabsTrigger value="payment-modes">Payment Modes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Monthly Revenue Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  <span>Monthly Revenue Trend</span>
                </CardTitle>
                <CardDescription>Revenue collection over the last 12 months</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics?.monthlyRevenue || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Batch Analysis Tab */}
        <TabsContent value="batch-analysis" className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span>Batch-wise Revenue Analysis</span>
                </CardTitle>
                <CardDescription>Revenue comparison across different student batches</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={analytics?.batchWiseRevenue || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="batchYear" />
                    <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                    <Tooltip 
                      formatter={(value: number, name: string) => [
                        name === 'totalRevenue' ? formatCurrency(value) : formatNumber(value),
                        name === 'totalRevenue' ? 'Total Revenue' : 'Total Students'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="totalRevenue" fill="#3b82f6" name="Total Revenue" />
                    <Bar dataKey="totalStudents" fill="#10b981" name="Total Students" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Batch Details Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Batch Performance Details</CardTitle>
                <CardDescription>Detailed breakdown of each batch's financial performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Batch Year</th>
                        <th className="text-right p-2">Students</th>
                        <th className="text-right p-2">Total Revenue</th>
                        <th className="text-right p-2">Avg Revenue/Student</th>
                        <th className="text-center p-2">Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics?.batchWiseRevenue?.map((batch, index) => (
                        <tr key={batch.batchYear} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{batch.batchYear}</td>
                          <td className="p-2 text-right">{formatNumber(batch.totalStudents)}</td>
                          <td className="p-2 text-right">{formatCurrency(batch.totalRevenue)}</td>
                          <td className="p-2 text-right">{formatCurrency(batch.averageRevenue)}</td>
                          <td className="p-2 text-center">
                            <Badge variant={batch.averageRevenue > 50000 ? "default" : "secondary"}>
                              {batch.averageRevenue > 50000 ? "High" : "Average"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Program Analysis Tab */}
        <TabsContent value="program-analysis" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Program Revenue Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Program-wise Revenue</CardTitle>
                  <CardDescription>Revenue distribution across programs</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.programWiseRevenue || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ programCode, percentage }) => `${programCode} (${percentage}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="totalRevenue"
                      >
                        {analytics?.programWiseRevenue?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* Program Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Program Performance</CardTitle>
                  <CardDescription>Detailed program metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics?.programWiseRevenue?.map((program, index) => (
                      <div key={program.programCode} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <div>
                            <p className="font-medium text-sm">{program.programCode}</p>
                            <p className="text-xs text-gray-600">{program.programName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(program.totalRevenue)}</p>
                          <p className="text-xs text-gray-600">{program.studentCount} students</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </TabsContent>

        {/* Payment Modes Tab */}
        <TabsContent value="payment-modes" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Mode Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Payment Mode Distribution</CardTitle>
                  <CardDescription>Breakdown by payment methods</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.paymentModeBreakdown || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ paymentMode, percentage }) => `${paymentMode} (${percentage.toFixed(1)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {analytics?.paymentModeBreakdown?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* Payment Mode Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method Statistics</CardTitle>
                  <CardDescription>Transaction counts and amounts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics?.paymentModeBreakdown?.map((mode, index) => (
                      <div key={mode.paymentMode} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <div>
                            <p className="font-medium text-sm">{mode.paymentMode}</p>
                            <p className="text-xs text-gray-600">{mode.count} transactions</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(mode.amount)}</p>
                          <p className="text-xs text-gray-600">{mode.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default AccountantAnalytics
